<template>
<div class="lend-modal-overlay" @click.self="$emit('close')">
    <div class="lend-modal compass-card" @click.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h3 style="font-size: 1.2rem; font-weight: 600; margin: 0;">
                {{ currentMode === 'supply' ? 'Supply' : 'Borrow' }} {{ token?.token_symbol }}
            </h3>
            <button class="btn-close" @click="$emit('close')"></button>
        </div>

        <!-- Mode toggle -->
        <div class="analytics-tabs mb-3">
            <button :class="['tab-btn', { active: currentMode === 'supply' }]" @click="currentMode = 'supply'">
                Supply (Lend)
            </button>
            <button :class="['tab-btn', { active: currentMode === 'borrow' }]" @click="currentMode = 'borrow'">
                Borrow
            </button>
        </div>

        <!-- Protocol selector -->
        <div class="mb-3">
            <label class="small" style="color: #6b7280;">Protocol</label>
            <el-select v-model="selectedProtocol" placeholder="Select protocol" class="w-100">
                <el-option
                    v-for="p in availableProtocols"
                    :key="p.protocol"
                    :label="capitalize(p.protocol) + ' — ' + formatApy(currentMode === 'supply' ? p.supply_apy : p.borrow_apy)"
                    :value="p.protocol"
                />
            </el-select>
        </div>

        <!-- Collateral selector (borrow mode only) -->
        <div v-if="currentMode === 'borrow'" class="mb-3">
            <label class="small" style="color: #6b7280;">Collateral Token</label>
            <el-select v-model="collateralToken" placeholder="Select collateral" class="w-100">
                <el-option
                    v-for="c in collateralOptions"
                    :key="c"
                    :label="c"
                    :value="c"
                />
            </el-select>
            <div class="small mt-1" style="color: #9ca3af;">
                Your existing deposits on this protocol are used as collateral.
            </div>
        </div>

        <!-- Amount input -->
        <div class="mb-3">
            <label class="small" style="color: #6b7280;">Amount</label>
            <el-input
                v-model="amount"
                type="number"
                :placeholder="'Enter ' + token?.token_symbol + ' amount'"
                size="large"
            >
                <template #append>
                    <span>{{ token?.token_symbol }}</span>
                </template>
            </el-input>
        </div>

        <!-- Rate info -->
        <div v-if="selectedProtocolData" class="compass-info-box mb-3" style="padding: 0.75rem;">
            <div class="row">
                <div class="col-6">
                    <div class="small" style="color: #6b7280;">Supply APY</div>
                    <div class="font-mono-num pct-change--positive">{{ formatApy(selectedProtocolData.supply_apy) }}</div>
                </div>
                <div class="col-6">
                    <div class="small" style="color: #6b7280;">Borrow APY</div>
                    <div class="font-mono-num">{{ formatApy(selectedProtocolData.borrow_apy) }}</div>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col-6">
                    <div class="small" style="color: #6b7280;">Utilization</div>
                    <div class="font-mono-num">{{ formatApy(selectedProtocolData.utilization) }}</div>
                </div>
                <div class="col-6">
                    <div class="small" style="color: #6b7280;">Est. {{ currentMode === 'supply' ? 'Earnings' : 'Cost' }} / Year</div>
                    <div class="font-mono-num" :class="currentMode === 'supply' ? 'pct-change--positive' : ''">
                        {{ estimatedYearly }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Health factor warning for borrow -->
        <div v-if="currentMode === 'borrow'" class="compass-info-box compass-info-box--warning mb-3" style="padding: 0.75rem;">
            <div class="small">
                <strong>Risk Warning:</strong> Borrowing requires collateral. If your health factor drops below 1.0,
                your collateral may be liquidated. Monitor your positions carefully.
            </div>
        </div>

        <!-- Action button -->
        <button
            class="compass-btn compass-btn--primary w-100"
            :disabled="!canSubmit"
            @click="executeAction"
        >
            <template v-if="executing">
                <span class="spinner-border spinner-border-sm me-1"></span> Processing...
            </template>
            <template v-else-if="!walletConnected">
                Connect Wallet
            </template>
            <template v-else>
                {{ currentMode === 'supply' ? 'Supply' : 'Borrow' }} {{ token?.token_symbol }}
            </template>
        </button>

        <div v-if="error" class="mt-2 small text-danger">{{ error }}</div>
        <div v-if="txSignature" class="mt-2 small" style="color: var(--compass-success);">
            Transaction sent:
            <a :href="'https://solscan.io/tx/' + txSignature" target="_blank" rel="noopener">
                {{ txSignature.slice(0, 12) }}...
            </a>
        </div>
    </div>
</div>
</template>

<script>
import { useSolSdk } from '../../composables/useSolSdk';
import { useLendFormatters } from '../../composables/useLendFormatters';

export default {
    name: 'LendDepositModal',
    props: {
        token: { type: Object, required: true },
        protocols: { type: Array, default: () => [] },
        mode: { type: String, default: 'supply' },
    },
    emits: ['close'],
    setup() {
        const { getSdk, isReady } = useSolSdk();
        const { capitalize, formatApy } = useLendFormatters();
        return { getSdk, sdkReady: isReady, capitalize, formatApy };
    },
    data() {
        return {
            currentMode: this.mode,
            selectedProtocol: '',
            collateralToken: 'SOL',
            amount: '',
            executing: false,
            error: null,
            txSignature: null,
        };
    },
    computed: {
        walletConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        availableProtocols() {
            if (!this.token?.protocols) return [];
            return this.token.protocols.filter(p => {
                if (this.currentMode === 'supply') return p.supply_apy > 0;
                return p.borrow_apy > 0;
            }).sort((a, b) => {
                if (this.currentMode === 'supply') return b.supply_apy - a.supply_apy;
                return a.borrow_apy - b.borrow_apy;
            });
        },
        selectedProtocolData() {
            if (!this.selectedProtocol || !this.token?.protocols) return null;
            return this.token.protocols.find(p => p.protocol === this.selectedProtocol);
        },
        collateralOptions() {
            // Common collateral tokens - user's existing deposits serve as collateral
            return ['SOL', 'USDC', 'USDT', 'JITOSOL', 'MSOL'].filter(
                t => t !== this.token?.token_symbol
            );
        },
        estimatedYearly() {
            if (!this.amount || !this.selectedProtocolData) return '—';
            const apy = this.currentMode === 'supply'
                ? this.selectedProtocolData.supply_apy
                : this.selectedProtocolData.borrow_apy;
            const yearly = parseFloat(this.amount) * (apy / 100);
            return yearly.toFixed(4) + ' ' + this.token.token_symbol;
        },
        canSubmit() {
            if (!this.walletConnected || !this.selectedProtocol || !(parseFloat(this.amount) > 0) || this.executing) {
                return false;
            }
            if (this.currentMode === 'borrow' && !this.collateralToken) {
                return false;
            }
            return true;
        },
    },
    watch: {
        mode(val) { this.currentMode = val; },
        currentMode() {
            // Re-select best protocol when switching modes
            this.selectedProtocol = '';
            this.$nextTick(() => {
                if (this.availableProtocols.length > 0) {
                    this.selectedProtocol = this.availableProtocols[0].protocol;
                }
            });
        },
    },
    mounted() {
        if (this.availableProtocols.length > 0) {
            this.selectedProtocol = this.availableProtocols[0].protocol;
        }
        this._onEscape = (e) => { if (e.key === 'Escape') this.$emit('close'); };
        document.addEventListener('keydown', this._onEscape);
    },
    beforeUnmount() {
        if (this._onEscape) document.removeEventListener('keydown', this._onEscape);
    },
    methods: {
        // capitalize, formatApy provided by useLendFormatters via setup()
        async executeAction() {
            if (!this.canSubmit) return;
            this.executing = true;
            this.error = null;
            this.txSignature = null;

            try {
                const sdk = await this.getSdk();
                const walletName = 'default';
                const token = this.token.token_symbol;
                const amount = parseFloat(this.amount);
                const protocol = this.selectedProtocol;

                let tx;
                if (this.currentMode === 'supply') {
                    tx = await sdk.lend.deposit(walletName, token, amount, protocol);
                } else {
                    tx = await sdk.lend.borrow(walletName, token, amount, this.collateralToken, protocol);
                }

                this.txSignature = tx?.signature || tx?.txid || tx;
            } catch (e) {
                console.error('[LendDepositModal] Transaction failed:', e);
                this.error = e.message || 'Transaction failed. Please try again.';
            } finally {
                this.executing = false;
            }
        },
    },
};
</script>

<style scoped>
.lend-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}
.lend-modal {
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 1.5rem;
}
</style>
